import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { COMPANY } from "@/data/departments";
import { Menu, X } from "lucide-react";
import { useSite, useUrl } from "@/context/SiteContext";
import { useDepartments } from "@/context/DepartmentsContext";
import { SmartLink } from "@/components/SmartLink";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const location = useLocation();
  const { departments } = useDepartments();
  const url = useUrl();
  const { t } = useSite();
  const items = departments || [];

  const closeAll = () => { setOpen(false); setDeptOpen(false); };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-black/90" data-testid="site-navbar">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 flex items-center justify-between h-24 md:h-28">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 md:gap-4 group">
          <img src="/logo-suntec.png" alt="SUNTEC" className="h-[63px] md:h-[74px] lg:h-[85px] w-auto" />
          <span className="font-display font-light uppercase text-[32px] md:text-[40px] lg:text-[48px] tracking-[0.05em] leading-none">SUNTEC</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-12">
          <NavLink to="/" end data-testid="nav-home" className={({isActive}) => `font-mono-label hover:underline underline-offset-4 ${isActive ? "text-black" : "text-black/70"}`}>Acasă</NavLink>

          <div className="relative" onMouseEnter={() => setDeptOpen(true)} onMouseLeave={() => setDeptOpen(false)}>
            <button data-testid="nav-departments-toggle" className="font-mono-label text-black/70 hover:text-black">Departamente ▾</button>
            {deptOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3" data-testid="departments-dropdown">
                <div className="bg-white border border-black w-[520px] grid grid-cols-2 gap-px bg-black">
                  {items.map((d) => (
                    <Link key={d.slug} to={url.dept(d.slug)} onClick={closeAll} data-testid={`dropdown-dept-${d.slug}`}
                      className="bg-white p-4 hover:bg-black hover:text-white transition-all duration-150">
                      <div className="font-mono-label text-[10px] opacity-60">{d.number}</div>
                      <div className="font-display font-bold uppercase text-sm mt-1">{d.name}</div>
                    </Link>
                  ))}
                  {items.length % 2 !== 0 && <div aria-hidden className="bg-white" />}
                </div>
              </div>
            )}
          </div>

          <NavLink to={url.blog} data-testid="nav-blog" className={({isActive}) => `font-mono-label hover:underline underline-offset-4 ${isActive ? "text-black" : "text-black/70"}`}>Blog</NavLink>
          <NavLink to={url.contact} data-testid="nav-contact" className={({isActive}) => `font-mono-label hover:underline underline-offset-4 ${isActive ? "text-black" : "text-black/70"}`}>Contact</NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <SmartLink to={t("btn.nav.cta.link", url.contact)} data-testid="nav-cta" className="font-mono-label bg-black text-white px-5 py-3 hover:bg-white hover:text-black border border-black transition-all">
            {t("btn.nav.cta.label")}
          </SmartLink>
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            <Link to="/" onClick={closeAll} data-testid="mobile-nav-home" className="font-mono-label py-2">Acasă</Link>
            <div>
              <button className="font-mono-label py-2" onClick={() => setDeptOpen(!deptOpen)} data-testid="mobile-departments-toggle">
                Departamente {deptOpen ? "−" : "+"}
              </button>
              {deptOpen && (
                <div className="pl-4 border-l border-black/30 flex flex-col gap-2 mt-2">
                  {items.map((d) => (
                    <Link key={d.slug} to={url.dept(d.slug)} onClick={closeAll} data-testid={`mobile-dept-${d.slug}`}
                      className="font-display uppercase text-sm py-1">{d.number} — {d.name}</Link>
                  ))}
                </div>
              )}
            </div>
            <Link to={url.blog} onClick={closeAll} data-testid="mobile-nav-blog" className="font-mono-label py-2">Blog</Link>
            <Link to={url.contact} onClick={closeAll} data-testid="mobile-nav-contact" className="font-mono-label py-2">Contact</Link>
            <SmartLink to={t("btn.nav.cta.link", url.contact)} onClick={closeAll} data-testid="mobile-nav-cta" className="font-mono-label bg-black text-white px-5 py-3 text-center border border-black">{t("btn.nav.cta.label")}</SmartLink>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useSite();
  const url = useUrl();
  const { departments } = useDepartments();
  const items = departments || [];
  const salesEmail = t("contact.sales.email");
  const salesPhones = [t("contact.sales.phone1"), t("contact.sales.phone2")].filter(Boolean);
  return (
    <footer className="bg-black text-white mt-24" data-testid="site-footer">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <img src="/logo-suntec-white.png" alt="SUNTEC" className="h-14 md:h-16 w-auto" />
            <span className="font-mono font-medium text-xl md:text-2xl tracking-tight">SUNTEC</span>
          </div>
          <p className="mt-4 text-white/60 text-base leading-relaxed">
            {t("footer.tagline")}
          </p>
        </div>
        <div className="md:col-span-5">
          <div className="font-mono-label text-white/50 mb-4">Departamente</div>
          <ul className="grid grid-cols-2 gap-2 text-sm">
            {items.map((d) => (
              <li key={d.slug}>
                <Link to={url.dept(d.slug)} className="hover:text-white text-white/70" data-testid={`footer-dept-${d.slug}`}>
                  {d.number} · {d.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="font-mono-label text-white/50 mb-4">Contact</div>
          <div className="text-sm space-y-2 text-white/80">
            <div>{t("footer.address")}</div>
            <a href={`mailto:${salesEmail}`} className="block hover:text-white">{salesEmail}</a>
            {salesPhones.map((p) => (
              <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="block hover:text-white">{p}</a>
            ))}
          </div>
          <div className="mt-6 flex gap-4">
            <Link to="/admin" className="font-mono-label text-white/40 hover:text-white" data-testid="footer-admin-link">Admin</Link>
            <button
              type="button"
              onClick={() => { if (typeof window !== "undefined" && window.__openCookieBanner) window.__openCookieBanner(); }}
              className="font-mono-label text-white/40 hover:text-white"
              data-testid="footer-cookies-link"
            >
              Preferințe cookies
            </button>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-6 flex justify-between items-center text-xs text-white/40 font-mono-label">
          <div>© {new Date().getFullYear()} SUNTEC GROUP</div>
          <div>{t("footer.copyright_right")}</div>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
